// Customer Support — company-user ("customer") side: raise complaints, track
// status, and chat with the master admin. Self-contained (only /support APIs).
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API = (p = '') => `${config.API_BASE_URL}/support${p}`;
const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

const TYPES = ['Billing', 'Technical', 'Feature Request', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High'];

const statusColor = (s) =>
  s === 'Open' ? 'warning' : s === 'In Progress' ? 'info' : s === 'Resolved' ? 'success' : 'default';
const prioColor = (p) => (p === 'High' ? 'error' : p === 'Medium' ? 'warning' : 'default');

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    Subject: '',
    ComplaintType: 'Technical',
    Priority: 'Medium',
    Description: '',
    Attachment: '',
  });
  const [saving, setSaving] = useState(false);

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API('/my'), { headers: headers() });
      if (r.ok) setTickets(await r.json());
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openTicket = async (id) => {
    setOpenId(id);
    setDetail(null);
    try {
      const r = await fetch(API(`/ticket/${id}`), { headers: headers() });
      if (r.ok) setDetail(await r.json());
    } catch {
      toast.error('Failed to open ticket');
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) {
      toast.warn('Attachment must be under 1.5 MB');
      return;
    }
    const rdr = new FileReader();
    rdr.onload = () => setForm((s) => ({ ...s, Attachment: rdr.result }));
    rdr.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.Subject.trim()) return toast.warn('Subject is required');
    setSaving(true);
    try {
      const r = await fetch(API('/ticket'), {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success('Complaint submitted');
        setForm({ Subject: '', ComplaintType: 'Technical', Priority: 'Medium', Description: '', Attachment: '' });
        load();
      } else {
        toast.error('Submit failed');
      }
    } catch {
      toast.error('Submit failed');
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      const r = await fetch(API(`/ticket/${openId}/reply`), {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ Message: reply }),
      });
      if (r.ok) {
        setReply('');
        openTicket(openId);
        load();
      } else {
        toast.error('Reply failed');
      }
    } catch {
      toast.error('Reply failed');
    }
  };

  /* ── Ticket detail view ── */
  if (openId) {
    const t = detail?.ticket;
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => setOpenId(null)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h6' fontWeight={800}>
            Ticket #{openId}
          </Typography>
        </Box>
        {!detail ? (
          <CircularProgress size={26} />
        ) : (
          <>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #E5E7EB', borderRadius: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Chip size='small' label={t.status} color={statusColor(t.status)} />
                <Chip size='small' label={t.priority} color={prioColor(t.priority)} variant='outlined' />
                <Chip size='small' label={t.complaintType} variant='outlined' />
              </Box>
              <Typography fontWeight={700}>{t.subject}</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                {t.description}
              </Typography>
              {t.attachment && (
                <a href={t.attachment} target='_blank' rel='noreferrer'>
                  📎 View attachment
                </a>
              )}
            </Paper>

            <Typography fontWeight={700} mb={1}>
              Conversation
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {detail.replies.length === 0 && (
                <Typography variant='body2' color='text.secondary'>
                  No replies yet.
                </Typography>
              )}
              {detail.replies.map((r) => (
                <Paper
                  key={r.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    maxWidth: '85%',
                    alignSelf: r.byMaster ? 'flex-start' : 'flex-end',
                    bgcolor: r.byMaster ? '#EEF2FF' : '#ECFDF5',
                  }}>
                  <Typography variant='caption' fontWeight={700}>
                    {r.byMaster ? '🛡️ Support' : r.byName}
                  </Typography>
                  <Typography variant='body2'>{r.message}</Typography>
                </Paper>
              ))}
            </Box>

            {t.status !== 'Closed' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Type a reply…'
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                />
                <Button variant='contained' onClick={sendReply}>
                  Send
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  /* ── List + create ── */
  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant='h5' fontWeight={800} mb={2}>
        🎫 Support / Complaints
      </Typography>

      {/* Create */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2, mb: 3 }}>
        <Typography fontWeight={700} mb={2}>
          Raise a Complaint
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size='small' label='Subject *' value={form.Subject}
              onChange={(e) => setForm({ ...form, Subject: e.target.value })} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size='small' select label='Type' value={form.ComplaintType}
              onChange={(e) => setForm({ ...form, ComplaintType: e.target.value })}>
              {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size='small' select label='Priority' value={form.Priority}
              onChange={(e) => setForm({ ...form, Priority: e.target.value })}>
              {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size='small' multiline minRows={3} label='Description'
              value={form.Description} onChange={(e) => setForm({ ...form, Description: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button component='label' variant='outlined' size='small'>
              {form.Attachment ? 'Attachment added ✓' : 'Attach (optional)'}
              <input type='file' hidden onChange={onFile} />
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
            <Button variant='contained' onClick={submit} disabled={saving || !form.Subject.trim()}
              startIcon={saving ? <CircularProgress size={16} color='inherit' /> : null}>
              {saving ? 'Submitting…' : 'Submit Complaint'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* My tickets */}
      <Typography fontWeight={700} mb={1}>
        My Complaints
      </Typography>
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#6B7280' }}>No complaints yet.</Box>
        ) : (
          tickets.map((t, i) => (
            <Box key={t.id}>
              {i > 0 && <Divider />}
              <Box
                onClick={() => openTicket(t.id)}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' }, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>
                    #{t.id} — {t.subject}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {t.complaintType} • {new Date(t.createdOn).toLocaleDateString('en-GB')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Chip size='small' label={t.priority} color={prioColor(t.priority)} variant='outlined' />
                  <Chip size='small' label={t.status} color={statusColor(t.status)} />
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
}
